/**
 * Rendering von Vertragsvorlagen. Die Syntax ist verbindlich, die Datenbank
 * verlässt sich auf die erzeugten Markup-Fragmente:
 *
 * - `{{path}}`                 Systemvariable (namespace.key) oder eigene Variable `{{var.key}}`
 * - `{{path|filter|filter}}`   Filter: money, date, percent, upper, lines
 * - `{{#if path}}…{{else}}…{{/if}}`  Bedingung, verschachtelbar; wahr = nicht leer, nicht 0, nicht false
 * - `{{signature:ROLE}}`       → `<div class="tk-signature" data-role="ROLE"></div>`
 * - fehlende fillable Variable → `<span class="tk-fill" data-var="KEY"></span>`
 *
 * Werte werden nie als rohes HTML eingesetzt: alles wird escaped, `lines` (und
 * address_block-Felder automatisch) erzeugt `<br>` erst nach dem Escapen.
 * Bleibt ein Platzhalter unaufgelöst, steht er unverändert im Ergebnis und in `unresolved`.
 * Wer ein „heute“ braucht, liefert es über context.dates.
 */
import { formatMoney } from "./money.js";
import { isIsoDate } from "./periods.js";
import {
  contextValue,
  isContractRole,
  systemVariable,
  type ContractContext,
  type ContractRole,
  type CustomVariableDef,
  type VariableType,
} from "./contract-variables.js";
import type { Currency, Language, MoneyLocale } from "./types.js";

export const ADDRESS_MASK = "•••";
export const CONTRACT_FILTERS = ["money", "date", "month", "percent", "upper", "lines"] as const;
export type ContractFilter = (typeof CONTRACT_FILTERS)[number];

/** Felder, die vor Vollsignatur in den Sichten brand/talent maskiert werden. */
export const MASKED_AGENCY_PATHS: readonly string[] = ["agency.address_block", "agency.street", "agency.postal_code", "agency.city"];

/**
 * Hinweise im Vorlagenkopf („Muster. Vor dem ersten Einsatz rechtlich prüfen lassen.“ oder interne Hinweise der Agentur):
 * stehen im Editor, nie im Vertrag (Befund 5). Der Renderer lässt sie weg; die Datenbank sperrt den Versand, falls doch einer bleibt.
 */
const SAMPLE_NOTICE = /<p class="tk-sample-notice">([\s\S]*?)<\/p>\s*/g;

/** Texte der Hinweise im Vorlagenkopf (für die Anzeige im Editor). */
export function sampleNotices(html: string | null | undefined): string[] {
  const out: string[] = [];
  for (const m of (html ?? "").matchAll(SAMPLE_NOTICE)) {
    const text = (m[1] ?? "").replace(/<[^>]*>/g, "").replace(/&amp;/g, "&").replace(/\s+/g, " ").trim();
    if (text) out.push(text);
  }
  return out;
}

/** Vertragstext ohne Hinweise im Vorlagenkopf. */
export function stripSampleNotices(html: string): string {
  return html.replace(SAMPLE_NOTICE, "");
}

/**
 * Platzhalter für die Vertragsnummer beim Versand: Die App rendert ihn in den Text, die Datenbank setzt die Nummer beim
 * Versand ein (Befund 26: keine Lücke im Nummernkreis, wenn der Versand scheitert).
 */
export const CONTRACT_NUMBER_SLOT = "[#TK-NR#]";

export function signatureMarkup(role: ContractRole): string {
  return `<div class="tk-signature" data-role="${role}"></div>`;
}

export function fillMarkup(key: string): string {
  return `<span class="tk-fill" data-var="${escapeHtml(key)}"></span>`;
}

/**
 * Vorlagentext für die Schweiz (VERTRAEGE-12): ss statt ß, Mehrwertsteuer (MWST) statt Umsatzsteuer, UID/MWST-Nr. statt
 * USt-IdNr. Gilt für deutschsprachige Verträge einer Agentur mit Locale de-CH.
 */
export function swissGerman(text: string): string {
  return text.replace(/ß/g, "ss").replace(/Umsatzsteuer/g, "Mehrwertsteuer").replace(/umsatzsteuer/g, "mehrwertsteuer")
    .replace(/USt-IdNr\./g, "UID/MWST-Nr.").replace(/\bUSt\b/g, "MWST")
    // K-400: länderneutrale Stellen „Umsatzsteuer bzw. Mehrwertsteuer“ nicht verdoppeln
    .replace(/Mehrwertsteuer(n?) bzw\. Mehrwertsteuer\1/g, "Mehrwertsteuer$1").replace(/MWST bzw\. MWST/g, "MWST");
}

export function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

export function languageOfLocale(locale: MoneyLocale): Language {
  return locale.startsWith("de") ? "de" : "en";
}

// ---------------------------------------------------------------------------
// Formatierung
// ---------------------------------------------------------------------------

/** ISO-Datum (oder Zeitstempel) → lokales Datum; alles andere unverändert als Text. */
export function formatContractDate(value: unknown, locale: MoneyLocale): string {
  if (typeof value !== "string") return value == null ? "" : String(value);
  const iso = value.length >= 10 ? value.slice(0, 10) : value;
  if (!isIsoDate(iso)) return value;
  return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" }).format(
    new Date(`${iso}T00:00:00Z`),
  );
}

/** Monatsschlüssel „2026-07“ (oder ein Datum) → „Juli 2026“ / „July 2026“ in der Locale der Agentur. */
export function formatContractMonth(value: unknown, locale: MoneyLocale): string {
  if (typeof value !== "string") return value == null ? "" : String(value);
  const key = /^\d{4}-(0[1-9]|1[0-2])$/.test(value) ? `${value}-01` : value.slice(0, 10);
  if (!isIsoDate(key)) return value;
  return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${key}T00:00:00Z`));
}

export function formatContractPercent(value: unknown, locale: MoneyLocale): string {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return value == null ? "" : String(value);
  const out = new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 2 }).format(n / 100).replace(/ /g, " ");
  // wie formatMoney: de-CH schreibt den Tausendertrenner als Apostroph; neuere ICU-Stände (Browser, Node 24) liefern „’“
  return locale === "de-CH" ? out.replace(/’/g, "'") : out;
}

export function formatContractNumber(value: unknown, locale: MoneyLocale): string {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return value == null ? "" : String(value);
  // ohne Tausendergruppierung: Jahre, Monate, Stückzahlen
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2, useGrouping: false }).format(n);
}

function formatContractMoney(value: unknown, currency: Currency, locale: MoneyLocale): string {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isSafeInteger(n)) return value == null ? "" : String(value);
  return formatMoney(n, currency, locale);
}

function formatBoolean(value: unknown, lang: Language): string {
  const yes = lang === "de" ? "Ja" : "Yes";
  const no = lang === "de" ? "Nein" : "No";
  if (typeof value === "string") return ["true", "1", "yes", "ja"].includes(value.trim().toLowerCase()) ? yes : no;
  return value ? yes : no;
}

// ---------------------------------------------------------------------------
// Tokenizer und Parser
// ---------------------------------------------------------------------------

const TAG = /\{\{\s*([^{}]*?)\s*\}\}/g;

export type PlaceholderKind = "var" | "if" | "else" | "endif" | "signature";

export interface Placeholder {
  kind: PlaceholderKind;
  /** Pfad (var/if) oder Rolle (signature); leer bei else/endif */
  path: string;
  filters: string[];
  raw: string;
}

/** Alle Platzhalter einer Vorlage in Reihenfolge (für die Prüfung). */
export function scanPlaceholders(source: string): Placeholder[] {
  const out: Placeholder[] = [];
  for (const t of tokenize(source)) if (t.type === "tag") out.push(classify(t.inner, t.raw));
  return out;
}

type Token = { type: "text"; text: string } | { type: "tag"; inner: string; raw: string };

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let last = 0;
  TAG.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = TAG.exec(source)) !== null) {
    if (m.index > last) tokens.push({ type: "text", text: source.slice(last, m.index) });
    tokens.push({ type: "tag", inner: m[1] ?? "", raw: m[0] });
    last = m.index + m[0].length;
  }
  if (last < source.length) tokens.push({ type: "text", text: source.slice(last) });
  return tokens;
}

function classify(inner: string, raw: string): Placeholder {
  if (inner === "else") return { kind: "else", path: "", filters: [], raw };
  if (inner === "/if") return { kind: "endif", path: "", filters: [], raw };
  // „{{#if pfad}}“ braucht Leerzeichen und Pfad; „{{#iffy}}“ oder „{{#if}}“ sind unbekannte Platzhalter (R-31)
  if (/^#if\s+\S/.test(inner)) return { kind: "if", path: inner.slice(3).trim(), filters: [], raw };
  if (inner.startsWith("signature:")) return { kind: "signature", path: inner.slice(10).trim(), filters: [], raw };
  const parts = inner.split("|").map((p) => p.trim());
  return { kind: "var", path: parts[0] ?? "", filters: parts.slice(1), raw };
}

type Node =
  | { kind: "text"; text: string }
  | { kind: "var"; path: string; filters: string[]; raw: string }
  | { kind: "if"; path: string; then: Node[]; otherwise: Node[]; raw: string }
  | { kind: "signature"; role: string; raw: string }
  /** unpassender Tag (else/endif ohne if, offenes if) – bleibt roh stehen */
  | { kind: "stray"; inner: string; raw: string };

interface ParseResult {
  nodes: Node[];
  pos: number;
  closedBy: "else" | "endif" | null;
}

function parseNodes(tokens: Token[], start: number, inIf: boolean): ParseResult {
  const nodes: Node[] = [];
  let pos = start;
  while (pos < tokens.length) {
    const t = tokens[pos]!;
    pos += 1;
    if (t.type === "text") {
      nodes.push({ kind: "text", text: t.text });
      continue;
    }
    const p = classify(t.inner, t.raw);
    if (p.kind === "else" || p.kind === "endif") {
      if (inIf) return { nodes, pos, closedBy: p.kind };
      nodes.push({ kind: "stray", inner: t.inner, raw: t.raw });
      continue;
    }
    if (p.kind === "signature") {
      nodes.push({ kind: "signature", role: p.path, raw: t.raw });
      continue;
    }
    if (p.kind === "var") {
      nodes.push({ kind: "var", path: p.path, filters: p.filters, raw: t.raw });
      continue;
    }
    // #if
    const thenPart = parseNodes(tokens, pos, true);
    pos = thenPart.pos;
    let otherwise: Node[] = [];
    let closedBy = thenPart.closedBy;
    let elseRaw: string | null = null;
    if (closedBy === "else") {
      elseRaw = tokens[pos - 1]?.type === "tag" ? (tokens[pos - 1] as { raw: string }).raw : "{{else}}";
      const elsePart = parseNodes(tokens, pos, true);
      pos = elsePart.pos;
      otherwise = elsePart.nodes;
      closedBy = elsePart.closedBy;
    }
    if (closedBy === "endif") {
      nodes.push({ kind: "if", path: p.path, then: thenPart.nodes, otherwise, raw: t.raw });
      continue;
    }
    // offenes if: Tag bleibt roh, Inhalt wird normal ausgegeben
    nodes.push({ kind: "stray", inner: t.inner, raw: t.raw });
    nodes.push(...thenPart.nodes);
    if (elseRaw !== null) {
      nodes.push({ kind: "stray", inner: "else", raw: elseRaw });
      nodes.push(...otherwise);
    }
  }
  return { nodes, pos, closedBy: null };
}

// ---------------------------------------------------------------------------
// Auflösung der Werte
// ---------------------------------------------------------------------------

type Resolved =
  | { status: "ok"; value: unknown; type: VariableType; lines: boolean }
  | { status: "fill"; key: string }
  | { status: "missing" }
  | { status: "unknown" };

interface Env {
  customVars: Map<string, CustomVariableDef>;
  values: Record<string, unknown>;
  context: ContractContext;
  locale: MoneyLocale;
  lang: Language;
  currency: Currency;
  mask: boolean;
  unresolved: Set<string>;
  /** Platzhalter, die im gewählten Zweig tatsächlich stehen (nicht im nicht gewählten {{else}}-Zweig) */
  used: Set<string>;
  signatureRoles: ContractRole[];
}

function isEmpty(value: unknown): boolean {
  return value === undefined || value === null || (typeof value === "string" && value.trim() === "");
}

function resolve(env: Env, path: string): Resolved {
  if (path.startsWith("var.")) {
    const key = path.slice(4);
    const def = env.customVars.get(key);
    if (!def) return { status: "unknown" };
    const lines = def.type === "lines";
    const value = env.values[key];
    if (!isEmpty(value)) return { status: "ok", value, type: def.type, lines };
    if (!isEmpty(def.default)) return { status: "ok", value: def.default, type: def.type, lines };
    if (def.fillable_by) return { status: "fill", key };
    if (def.required) return { status: "missing" };
    return { status: "ok", value: "", type: def.type, lines };
  }
  const sv = systemVariable(path);
  if (!sv) return { status: "unknown" };
  if (env.mask && MASKED_AGENCY_PATHS.includes(path)) return { status: "ok", value: ADDRESS_MASK, type: "text", lines: false };
  const value = contextValue(env.context, path);
  if (value === undefined || value === null) return { status: "missing" };
  const lines = sv.type === "lines" || path.endsWith(".address_block");
  return { status: "ok", value, type: sv.type, lines };
}

function truthy(value: unknown): boolean {
  if (value === undefined || value === null || value === false) return false;
  if (typeof value === "number") return value !== 0 && !Number.isNaN(value);
  if (typeof value === "string") {
    const s = value.trim();
    return s !== "" && s !== "0" && s.toLowerCase() !== "false";
  }
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function toText(value: unknown, type: VariableType, env: Env): string {
  if (Array.isArray(value)) return value.map((v) => toText(v, type, env)).join("\n");
  switch (type) {
    case "money":
      return formatContractMoney(value, env.currency, env.locale);
    case "date":
      return formatContractDate(value, env.locale);
    case "percent":
      return formatContractPercent(value, env.locale);
    case "number":
      return formatContractNumber(value, env.locale);
    case "boolean":
      return formatBoolean(value, env.lang);
    default:
      return value == null ? "" : String(value);
  }
}

/** Filterkette anwenden; null = unbekannter Filter. */
function applyFilters(value: unknown, type: VariableType, lines: boolean, filters: string[], env: Env): string | null {
  let acc: unknown = value;
  let accType: VariableType = type;
  let br = lines || Array.isArray(value);
  for (const f of filters) {
    switch (f) {
      case "money":
        acc = formatContractMoney(acc, env.currency, env.locale);
        accType = "text";
        break;
      case "date":
        acc = formatContractDate(acc, env.locale);
        accType = "text";
        break;
      case "month":
        acc = formatContractMonth(acc, env.locale);
        accType = "text";
        break;
      case "percent":
        acc = formatContractPercent(acc, env.locale);
        accType = "text";
        break;
      case "upper":
        acc = toText(acc, accType, env).toUpperCase();
        accType = "text";
        break;
      case "lines":
        br = true;
        break;
      default:
        return null;
    }
  }
  const text = escapeHtml(toText(acc, accType, env));
  return br ? text.replace(/\r\n|\r|\n/g, "<br>") : text;
}

function renderNodes(nodes: Node[], env: Env): string {
  let out = "";
  for (const n of nodes) {
    switch (n.kind) {
      case "text":
        // Schweizer Agentur (Locale de-CH): Schweizer Schreibweise und Begriffe im Vorlagentext, nie in eingesetzten Werten
        out += env.locale === "de-CH" ? swissGerman(n.text) : n.text;
        break;
      case "stray":
        env.unresolved.add(n.inner);
        out += n.raw;
        break;
      case "signature":
        if (isContractRole(n.role)) {
          env.signatureRoles.push(n.role);
          out += signatureMarkup(n.role);
        } else {
          env.unresolved.add(`signature:${n.role}`);
          out += n.raw;
        }
        break;
      case "if": {
        const r = resolve(env, n.path);
        if (r.status === "unknown") env.unresolved.add(n.path);
        // Systemwert gar nicht geliefert (nicht: bewusst leer): melden, statt die Klausel still wegzulassen (R-17)
        else if (r.status === "missing" && !n.path.startsWith("var.") && contextValue(env.context, n.path) === undefined) env.unresolved.add(n.path);
        const cond = r.status === "ok" && truthy(r.value);
        out += renderNodes(cond ? n.then : n.otherwise, env);
        break;
      }
      case "var": {
        env.used.add(n.path);
        const r = resolve(env, n.path);
        if (r.status === "fill") {
          out += fillMarkup(r.key);
          break;
        }
        if (r.status !== "ok") {
          env.unresolved.add(n.path);
          out += n.raw;
          break;
        }
        const text = applyFilters(r.value, r.type, r.lines, n.filters, env);
        if (text === null) {
          env.unresolved.add([n.path, ...n.filters].join("|"));
          out += n.raw;
          break;
        }
        out += text;
        break;
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Öffentliche Schnittstelle
// ---------------------------------------------------------------------------

export interface RenderContractInput {
  bodyHtml: string;
  annexHtml?: string | null;
  customVars: CustomVariableDef[];
  values: Record<string, unknown>;
  context: ContractContext;
  locale: MoneyLocale;
  currency: Currency;
  view: ContractRole;
  /** Rollen, die die Anlage nie sehen */
  annexHiddenFrom: ContractRole[];
  /** Anschrift der Agentur bis zur Vollsignatur in den Sichten brand/talent maskieren */
  maskAgencyAddressUntilComplete?: boolean;
  allSigned?: boolean;
}

export interface RenderContractResult {
  html: string;
  /** null, wenn es keine Anlage gibt oder die Sicht sie nicht sehen darf */
  annexHtml: string | null;
  /** unaufgelöste Platzhalter (Pfad, ggf. mit Filtern), ohne Dubletten */
  unresolved: string[];
  /** Signaturrollen in Reihenfolge ihres Auftretens (Text, dann Anlage), ohne Dubletten */
  signatureRoles: ContractRole[];
  /** Platzhalter im gewählten Zweig (Pfad ohne Filter), ohne Dubletten; nicht gewählte {{#if}}-Zweige zählen nicht */
  used: string[];
}

export function renderContract(input: RenderContractInput): RenderContractResult {
  const env: Env = {
    customVars: new Map(input.customVars.map((d) => [d.key, d])),
    values: input.values ?? {},
    context: input.context ?? {},
    locale: input.locale,
    lang: languageOfLocale(input.locale),
    currency: input.currency,
    mask: !!input.maskAgencyAddressUntilComplete && !input.allSigned && input.view !== "agency",
    unresolved: new Set(),
    used: new Set(),
    signatureRoles: [],
  };
  const html = renderNodes(parseNodes(tokenize(stripSampleNotices(input.bodyHtml ?? "")), 0, false).nodes, env);
  let annexHtml: string | null = null;
  const annexVisible = !input.annexHiddenFrom.includes(input.view);
  if (input.annexHtml != null && input.annexHtml !== "" && annexVisible) {
    annexHtml = renderNodes(parseNodes(tokenize(stripSampleNotices(input.annexHtml)), 0, false).nodes, env);
  }
  return {
    html,
    annexHtml,
    unresolved: [...env.unresolved],
    signatureRoles: [...new Set(env.signatureRoles)],
    used: [...env.used],
  };
}

// Sprache, Formate und Beweiskette des Vertragsbereichs (gleiche Einstiegsstelle wie der Renderer)
export * from "./contract-locale.js";
export * from "./contract-evidence.js";
export * from "./contract-term.js";
export * from "./contract-pdf.js";
// Positivliste der Auszeichnungen und Kopfzeile des vertraulichen Teils (VB-25, VB-20; gleich in der Datenbank, 0111)
export * from "./contract-markup.js";
